// src/pages/pharmacy/PharmacySubscription.js
import React, { useState, useEffect } from 'react';
import { 
  CreditCard, Check, X, Loader2, AlertCircle, 
  Package, TrendingUp, Star,  Info
} from 'lucide-react';
import apiService from '../../services/api';

const PharmacySubscription = () => {
  const [loading, setLoading] = useState(true);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [plans, setPlans] = useState(null);
  const [subscribing, setSubscribing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [transactionCode, setTransactionCode] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [subscriptionRes, plansRes] = await Promise.all([
        apiService.pharmacies.getSubscription(),
        apiService.pharmacies.getSubscriptionPlans()
      ]);

      setCurrentSubscription(subscriptionRes.data);
      setPlans(plansRes.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planKey) => {
    if (planKey === 'free') {
      try {
        setSubscribing(true);
        await apiService.pharmacies.subscribe({
          plan: 'free'
        });
        setSuccess('Successfully subscribed to Free plan');
        fetchData();
      } catch (err) {
        setError(err.message);
      } finally {
        setSubscribing(false);
      }
      return;
    }

    setSelectedPlan(planKey);
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    
    if (!phoneNumber || phoneNumber.length < 10) {
      setError('Please enter a valid phone number');
      return;
    }

    if (!transactionCode || transactionCode.trim().length < 8) {
      setError('Please enter a valid M-Pesa transaction code');
      return;
    }

    const phoneRegex = /^(254|0)[17]\d{8}$/;
    if (!phoneRegex.test(phoneNumber.replace(/\s+/g, ''))) {
      setError('Please enter a valid Kenyan phone number (e.g., 0712345678 or 254712345678)');
      return;
    }

    try {
      setSubscribing(true);
      setError(null);

      await apiService.pharmacies.subscribe({
        plan: selectedPlan,
        phoneNumber: phoneNumber.trim(),
        transactionCode: transactionCode.trim().toUpperCase(),
        paymentMethod: 'mpesa'
      });

      setSuccess('Payment submitted successfully! Your subscription will be activated once admin verifies the payment.');
      setSelectedPlan(null);
      setPhoneNumber('');
      setTransactionCode('');
      fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubscribing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will be downgraded to the free plan.')) {
      return;
    }

    try {
      setLoading(true);
      await apiService.pharmacies.cancelSubscription();
      setSuccess('Subscription cancelled successfully. Reverted to free plan.');
      fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const getPlanIcon = (planKey) => {
    const icons = {
      free: Package,
      basic: TrendingUp,
      premium: Star
    };
    return icons[planKey] || Package;
  };

  const getPlanColor = (planKey) => {
    const colors = {
      free: 'gray',
      basic: 'blue',
      premium: 'purple'
    };
    return colors[planKey] || 'gray';
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-8">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h1 className="text-2xl font-bold text-gray-900">Subscription Plans</h1>
        <p className="text-gray-600 mt-1">
          Choose the perfect plan for your pharmacy
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-800">{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-sm text-red-600 hover:text-red-800 mt-1"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start space-x-3">
          <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-green-800">{success}</p>
            <button
              onClick={() => setSuccess(null)}
              className="text-sm text-green-600 hover:text-green-800 mt-1"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Current Subscription */}
      {currentSubscription && (
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-2 capitalize">
                {currentSubscription.plan} Plan
              </h2>
              <p className="text-blue-100 mb-4">
                {plans[currentSubscription.plan]?.description}
              </p>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${
                    currentSubscription.status === 'active' ? 'bg-green-400' : 
                    currentSubscription.status === 'cancelled' ? 'bg-red-400' : 
                    'bg-yellow-400'
                  }`} />
                  <span className="text-sm capitalize">{currentSubscription.status}</span>
                </div>
                
                {currentSubscription.currentPeriodEnd && (
                  <p className="text-sm text-blue-100">
                    {currentSubscription.status === 'active' ? 'Renews' : 'Valid until'}: {' '}
                    {new Date(currentSubscription.currentPeriodEnd).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>

            {currentSubscription.plan !== 'free' && currentSubscription.status === 'active' && (
              <button
                onClick={handleCancelSubscription}
                className="px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-medium"
              >
                Cancel Subscription
              </button>
            )}
          </div>
        </div>
      )}

      {/* Subscription Plans */}
      <div className="grid md:grid-cols-3 gap-6">
        {plans && Object.entries(plans).map(([key, plan]) => {
          const Icon = getPlanIcon(key);
          const color = getPlanColor(key);
          const isCurrentPlan = currentSubscription?.plan === key;
          const isPremium = key === 'premium';

          return (
            <div
              key={key}
              className={`bg-white rounded-lg shadow-lg border-2 transition-all hover:shadow-xl ${
                isCurrentPlan 
                  ? `border-${color}-500` 
                  : isPremium 
                  ? 'border-purple-200' 
                  : 'border-gray-200'
              } ${isPremium ? 'relative' : ''}`}
            >
              {isPremium && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-purple-600 to-purple-700 text-white px-4 py-1 rounded-full text-sm font-medium">
                  Most Popular
                </div>
              )}

              <div className="p-6">
                {/* Plan Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 rounded-lg bg-${color}-100 flex items-center justify-center`}>
                    <Icon className={`w-6 h-6 text-${color}-600`} />
                  </div>
                  {isCurrentPlan && (
                    <span className="text-xs font-semibold text-green-600 bg-green-100 px-2 py-1 rounded-full">
                      Current Plan
                    </span>
                  )}
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <p className="text-gray-600 text-sm mb-4">{plan.description}</p>

                {/* Pricing */}
                <div className="mb-6">
                  <div className="flex items-baseline">
                    <span className="text-4xl font-bold text-gray-900">
                      {plan.price === 0 ? 'Free' : `KES ${plan.price.toLocaleString()}`}
                    </span>
                    {plan.price > 0 && (
                      <span className="text-gray-600 ml-2">/month</span>
                    )}
                  </div>
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start space-x-2">
                    {plan.features.listingsLimit === null ? (
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    )}
                    <span className="text-sm text-gray-700">
                      {plan.features.listingsLimit === null 
                        ? 'Unlimited product listings' 
                        : `Up to ${plan.features.listingsLimit} product listings`}
                    </span>
                  </li>

                  <li className="flex items-start space-x-2">
                    {plan.features.orderManagement ? (
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <X className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    )}
                    <span className={`text-sm ${plan.features.orderManagement ? 'text-gray-700' : 'text-gray-400'}`}>
                      Order management
                    </span>
                  </li>

                  <li className="flex items-start space-x-2">
                    {plan.features.deliveryTracking ? (
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <X className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    )}
                    <span className={`text-sm ${plan.features.deliveryTracking ? 'text-gray-700' : 'text-gray-400'}`}>
                      Delivery tracking
                    </span>
                  </li>

                  <li className="flex items-start space-x-2">
                    {plan.features.analytics ? (
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <X className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    )}
                    <span className={`text-sm ${plan.features.analytics ? 'text-gray-700' : 'text-gray-400'}`}>
                      Analytics dashboard
                    </span>
                  </li>

                  <li className="flex items-start space-x-2">
                    {plan.features.prescriptionVerification ? (
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <X className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    )}
                    <span className={`text-sm ${plan.features.prescriptionVerification ? 'text-gray-700' : 'text-gray-400'}`}>
                      Prescription verification
                    </span>
                  </li>

                  <li className="flex items-start space-x-2">
                    {plan.features.prioritySupport ? (
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <X className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    )}
                    <span className={`text-sm ${plan.features.prioritySupport ? 'text-gray-700' : 'text-gray-400'}`}>
                      Priority support
                    </span>
                  </li>
                </ul>

                {/* CTA Button */}
                <button
                  onClick={() => handleSubscribe(key)}
                  disabled={subscribing || isCurrentPlan || key === 'free'}
                  className={`w-full py-3 px-4 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 ${
                    isCurrentPlan 
                      ? 'bg-gray-100 text-gray-400' 
                      : isPremium
                      ? 'bg-purple-600 hover:bg-purple-700 text-white'
                      : key === 'basic'
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-gray-600 hover:bg-gray-700 text-white'
                  }`}
                >
                  {subscribing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : isCurrentPlan ? (
                    <span>Current Plan</span>
                  ) : key === 'free' ? (
                    <span>Free Plan</span>
                  ) : (
                    <span>Upgrade Now</span>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Payment Modal - MOBILE RESPONSIVE VERSION */}
      {selectedPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[95vh] overflow-y-auto p-4 sm:p-6">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">
              Subscribe to {plans[selectedPlan].name} Plan
            </h3>

            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border mb-4 sm:mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm sm:text-base text-gray-600">Plan:</span>
                <span className="text-sm sm:text-base font-semibold text-gray-900">{plans[selectedPlan].name}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm sm:text-base text-gray-600">Amount:</span>
                <span className="font-bold text-lg sm:text-xl text-gray-900">
                  KES {plans[selectedPlan].price.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Payment Instructions - Mobile Optimized */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
              <div className="flex items-start gap-2 sm:gap-3">
                <Info className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="space-y-2 min-w-0 flex-1">
                  <h4 className="font-semibold text-sm sm:text-base text-blue-900">M-Pesa Payment Instructions</h4>
                  <ol className="text-xs sm:text-sm text-blue-800 space-y-1 list-decimal list-inside">
                    <li className="break-words">Go to M-Pesa menu on your phone</li>
                    <li className="break-words">Select <strong>Lipa na M-Pesa</strong></li>
                    <li className="break-words">Select <strong>Pay Bill</strong></li>
                    <li className="break-words">Enter Business Number: <strong className="text-blue-900">522522</strong></li>
                    <li className="break-words">Enter Account Number: <strong className="text-blue-900 break-all">MEDIGO{plans[selectedPlan].price}</strong></li>
                    <li className="break-words">Enter Amount: <strong className="text-blue-900">KES {plans[selectedPlan].price.toLocaleString()}</strong></li>
                    <li className="break-words">Enter your M-Pesa PIN and confirm</li>
                    <li className="break-words">You will receive a confirmation SMS with transaction code</li>
                    <li className="break-words">Enter your phone number and transaction code below</li>
                  </ol>
                </div>
              </div>
            </div>

            <form onSubmit={handlePayment} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  M-Pesa Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="0712345678 or 254712345678"
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  disabled={subscribing}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Phone number you used to make the payment
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  M-Pesa Transaction Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={transactionCode}
                  onChange={(e) => setTransactionCode(e.target.value.toUpperCase())}
                  placeholder="e.g., QGH7D8F9KL"
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                  required
                  disabled={subscribing}
                />
                <p className="text-xs text-gray-500 mt-1">
                  10-character code from your M-Pesa confirmation SMS
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedPlan(null);
                    setPhoneNumber('');
                    setTransactionCode('');
                  }}
                  className="w-full sm:flex-1 py-2.5 sm:py-2 px-4 text-sm sm:text-base border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  disabled={subscribing}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={subscribing || !phoneNumber.trim() || !transactionCode.trim()}
                  className="w-full sm:flex-1 py-2.5 sm:py-2 px-4 text-sm sm:text-base bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 font-medium"
                >
                  {subscribing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4" />
                      <span>Submit Payment</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PharmacySubscription;